import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { match } from "ts-pattern";
import { container } from "tsyringe";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import PostAuthorInfo from "components/pages/ForumPost/components/PostHeader/PostAuthorInfo";
import ModuleConfig from "configs/ModuleConfig";
import { useProjectId } from "hooks/useProjectId";
import { translateISO369 } from "utils/I18nText";
import CommentService from "services/projects/CommentService";
import ForumPostLikeButton from "../../components/ForumPostLikeButon";
import classes from "./classes.module.scss";

type IProps = {
	post: ForumPostResponseResource;
};

const modules = container.resolve(ModuleConfig).get().modules;
const commentService = container.resolve(CommentService);

export default function PostCard(props: IProps) {
	const { commentsCount } = usePostInfo(props.post);
	const { redirectToPost } = usePostNavigation(props.post);

	return (
		<div className={classes["root"]} onClick={redirectToPost}>
			<div className={classes["header"]}>
				<Typography type="h4" weight="bold">
					{translateISO369(props.post.title)}
				</Typography>

				<Typography type="p" weight="regular" size="medium" className={classes["pre-line"]}>
					{getDescription(translateISO369(props.post.description))}
				</Typography>
			</div>

			<div className={classes["info-container"]}>
				<PostAuthorInfo post={props.post} />

				<div className={classes["post-info"]}>
					<div className={classes["replies-info"]}>
						<MessageCircle className={classes["bubble-icon"]} />
						<Typography type="p" size="small" weight="regular" color="primary">
							{I18n.trslt(
								match(commentsCount)
									.with(0, () => I18n.asset.projects.forum.no_answers)
									.with(1, () => I18n.asset.projects.forum.single_answer)
									.otherwise(() => I18n.asset.projects.forum.answers),
								{ n: commentsCount.toFixed() },
							)}
						</Typography>
					</div>

					<ForumPostLikeButton post={props.post} />
				</div>
			</div>
		</div>
	);
}

const usePostInfo = (post: Pick<ForumPostResponseResource, "id">) => {
	const { projectId } = useProjectId();
	const [commentsCount, setCommentsCount] = useState<number>(0);

	const fetchCommentsCount = useCallback(async () => {
		return commentService.count(projectId, post.id).then(({ count }) => setCommentsCount(count));
	}, [post.id, projectId]);

	useEffect(() => {
		fetchCommentsCount();
	}, [fetchCommentsCount]);

	return { commentsCount };
};

const usePostNavigation = (post: Pick<ForumPostResponseResource, "id">) => {
	const { projectId } = useProjectId();
	const navigate = useNavigate();

	const redirectToPost = useCallback(() => {
		navigate(modules.pages.ForumPost.props.path.replace(":projectId", projectId).replace(":postId", post.id));
	}, [navigate, post.id, projectId]);

	return { redirectToPost };
};

function getDescription(description: string | null) {
	if (!description) return null;
	const maxLength = 120;
	if (description.length > maxLength) {
		return description.slice(0, maxLength).concat("...");
	}
	return description;
}
